import { ChangeDetectorRef, Component, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import {
  VisibleOrganization,
  Organization,
  FinanceSource,
  AuctionService,
  BidListService,
  FinanceSourceService,
  VisibleOrganizationService,
  LeadBuyerState,
  DeliveryIncluded,
  AddToBidList,
  LaunchDarklyService,
  UserProfileState,
  UpdateSelectedDealership
} from 'remarketing-angular-library';
import { catchError, of, Subject, take, takeUntil, tap } from 'rxjs';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { ALL_DEALERS } from 'src/search/components/inventory-item/inventory-item.component';
import { AnimatedNotificationComponent } from 'src/search/components/animated-notification/animated-notification.component';
import { AddRemoveBidListButtonEnum } from './add-remove-bid-list-button.enum';
import { CommonModule, NgClass } from '@angular/common';
import { DropdownSelectComponent } from 'remarketing-ui-components-lib';

@Component({
    selector: 'app-add-remove-bid-list-button',
    templateUrl: './add-remove-bid-list-button.component.html',
    styleUrls: ['./add-remove-bid-list-button.component.scss'],
    imports: [NgClass, DropdownSelectComponent, CommonModule]
})
export class AddRemoveBidListButtonComponent extends SaComponent implements OnInit {
  @Input() onBidList: boolean;
  @Input() auctionItemUuid: string;
  @Input() inventoryItemUuid: string;
  @Input() translations: any;
  @ViewChild('vehicleCard') vehicleCard;
  @Output() errorMessage = new Subject<string>();
  userUuid: string;
  modalConfig: any;
  modalRef: BsModalRef;
  dealershipOptions: any;
  dealershipValues: Array<VisibleOrganization> = [];
  allDealerships: Array<Organization> = [];
  financeSourceValues: Array<FinanceSource> = [];
  financeSourceOptions: any;
  selectedFinanceSource = '';
  selectedFinanceSourceObject: FinanceSource;
  selectedDeliveryOption = 'false';
  deliveryOptions: any;
  isOnBidList = false;
  selectedDealership: string;
  searchDealership: Organization;
  buyerOeUuid: string;
  modalOpening = false;
  inModal = false;
  enableFocusTrap = false;
  isLeadBuyer: boolean;

  get isBidlistSuccessModalEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('bidlistSuccessModalEnabled', false);
  }

  get isDeliveryEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('deliveryEnabled', false);
  }

  get isAllDealers(): boolean {
    return !this.isOnBidList && ALL_DEALERS === this.searchDealership.oeName;
  }

  constructor(
    readonly changeDetector: ChangeDetectorRef,
    public modalService: BsModalService,
    private readonly store: Store,
    private readonly auctionService: AuctionService,
    private readonly visibleOrganizationService: VisibleOrganizationService,
    private readonly financeSourceService: FinanceSourceService,
    private readonly bidListService: BidListService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly analyticsService: AnalyticsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isOnBidList = this.onBidList;
    this.setLeadBuyerDealership();
    this.buyerOeUuid = this.getOrganization().oeUuid;
    this.setNonLeadBuyerDefaultDealership();
  }

  setNonLeadBuyerDefaultDealership() {
    this.isLeadBuyer = this.store.selectSnapshot(UserProfileState.userProfileState).userProfile.isLeadBuyer;
    if (!this.isLeadBuyer) {
      // set Default Dealership for non-lead buyers
      this.store.dispatch(new UpdateSelectedDealership(this.getOrganization()));
    }
  }

  setLeadBuyerDealership() {
    this.store
      .select(state => state.leadBuyer || {})
      .pipe(takeUntil(this.destroyed$))
      .subscribe(leadBuyer => {
        this.searchDealership = leadBuyer.selectedDealership || ({ oeName: 'No Dealership Selected' } as Organization);
      });
  }

  getOrganization(): Organization {
    return this.store.selectSnapshot(state => state.org.organization) as Organization;
  }

  getDealershipOrganizationUuid(): string {
    return this.store.selectSnapshot(LeadBuyerState.leadBuyerState).selectedDealership.oeUuid;
  }

  handleToggleBidListClick(dealersChoice: TemplateRef<any>) {
    if (this.isAllDealers && this.isLeadBuyer) {
      this.populateDealershipDropdown();
      this.populateFinanceSourceDropdown();
      this.populateDeliveryDropdown();
      this.modalConfig = {
        class: 'modal--dealers-choice',
        backdrop: true
      };
      this.modalRef = this.modalService.show(dealersChoice, this.modalConfig);
      this.modalRef.setClass('modal--dealers-choice');
      this.modalOpening = true;
      this.inModal = true;
    } else if (this.isOnBidList) {
      // remove it
      this.removeItemFromBidList();
    } else {
      // add it
      this.toggleOnBidList();
      this.addItemToBidList();
    }
  }

  modalFocus() {
    if (this.modalOpening) {
      this.modalOpening = false;
      document.getElementById('Dealership-title').focus();
    }
  }

  submitDealership() {
    this.modalRef.hide();
    this.inModal = false;
    this.toggleOnBidList();
    this.addToBidlistFromModal();
  }

  closeDealership() {
    this.modalRef.hide();
    this.inModal = false;
    this.focusOnButton(this.vehicleCard.nativeElement.querySelector('button.add-to-bidlist'));
  }

  focusOnButton(button) {
    button.focus();
  }

  updateDealership(option) {
    if (option && option.dataset && option.dataset.value) {
      this.buyerOeUuid = option.dataset.value;
      this.populateFinanceSourceDropdown();
      this.populateDeliveryDropdown();
    }
  }

  updateFinanceSource(option) {
    if (option && option.dataset && option.dataset.value) {
      this.selectedFinanceSource = option.dataset.value;
    }
  }

  updateDelivery(option) {
    if (option && option.dataset && option.dataset.value) {
      this.selectedDeliveryOption = option.dataset.value;
    }
  }

  addToBidlistFromModal() {
    const financeSourceAch = this.financeSourceValues.find(
      source => source.financeSourceUuid === this.selectedFinanceSource
    )?.achSelected;

    this.bidListService
      .addItemToBidList(
        this.buyerOeUuid,
        this.inventoryItemUuid,
        this.auctionItemUuid,
        this.selectedDeliveryOption === 'true',
        this.selectedFinanceSource,
        financeSourceAch
      )
      .pipe(take(1))
      .subscribe(
        () => {
          if (this.isBidlistSuccessModalEnabled) {
            this.openModal(AnimatedNotificationComponent, { message: this.translations.addToBidlistSucess });
          }
          // Update the search results state so that it's persisted if we don't search again.
          this.store.dispatch(new AddToBidList(this.inventoryItemUuid));
          this.addRemoveAnalyticsClickEvent('Add to');
        },
        error => {
          this.errorMessage.next(AddRemoveBidListButtonEnum.ADD);
          this.toggleOnBidList();
        }
      );
  }

  openModal(template, initialState) {
    this.modalConfig = {
      class: 'modal--animated-notification',
      backdrop: false,
      initialState
    };
    this.modalRef = this.modalService.show(template, this.modalConfig);
    this.modalRef.setClass('modal--animated-notification');
    setTimeout(() => {
      this.modalRef.hide();
    }, 2000);
  }

  populateDeliveryDropdown() {
    this.auctionService
      .getDeliveryOption(this.auctionItemUuid, this.buyerOeUuid)
      .pipe(take(1))
      .subscribe(
        (result: DeliveryIncluded) => {
          let options;
          const localDelivery = result.deliveryIncluded;
          if (localDelivery !== undefined && localDelivery !== null) {
            if (localDelivery) {
              options = [
                { name: 'Yes', value: 'true', selected: true },
                { name: 'No', value: 'false' }
              ];
            } else {
              options = [
                { name: 'Yes', value: 'true' },
                { name: 'No', value: 'false', selected: true }
              ];
            }
          } else {
            options = [{ name: this.translations.notAvailable, value: 'false', selected: true }];
          }
          this.deliveryOptions = [{ options }];
          this.selectedDeliveryOption = options.find(option => option.selected).value;
        },
        error => {
          console.error(error);
        }
      );
  }

  addItemToBidList() {
    let localDelivery;
    let financeSourceUuid;
    let financeSourceAch;

    if (this.getDealershipOrganizationUuid() === this.getOrganization().oeUuid) {
      this.financeSourceService
        .getFinanceSourceListByOeUuid(this.getOrganization().oeUuid)
        .pipe(take(1))
        .subscribe(
          financeSources => {
            this.financeSourceValues = financeSources;
            const sources = this.financeSourceValues.map(source => {
              return {
                name: source.financeSourceName,
                value: source.financeSourceUuid,
                selected: source.defaultIndicator
              };
            });
            if (!sources.some(item => item.selected)) {
              this.selectedFinanceSourceObject = this.financeSourceValues[0];
            } else {
              this.selectedFinanceSourceObject = this.financeSourceValues.find(item => item.defaultIndicator);
            }
            financeSourceUuid = this.selectedFinanceSourceObject.financeSourceUuid;
            financeSourceAch = this.selectedFinanceSourceObject.achSelected;
          },
          error => {
            console.error(error);
          }
        );
    } else {
      financeSourceUuid = undefined;
      financeSourceAch = undefined;
    }

    this.auctionService
      .getDeliveryOption(this.auctionItemUuid, this.getDealershipOrganizationUuid())
      .pipe(take(1))
      .subscribe((result: DeliveryIncluded) => {
        localDelivery = result.deliveryIncluded;
        this.bidListService
          .addItemToBidList(
            this.getDealershipOrganizationUuid(),
            this.inventoryItemUuid,
            this.auctionItemUuid,
            localDelivery,
            financeSourceUuid,
            financeSourceAch
          )
          .pipe(take(1))
          .subscribe(
            () => {
              if (this.isBidlistSuccessModalEnabled) {
                this.openModal(AnimatedNotificationComponent, { message: this.translations.addToBidlistSucess });
              }
              // Update the search results state so that it's persisted if we don't search again.
              this.store.dispatch(new AddToBidList(this.inventoryItemUuid));
              this.addRemoveAnalyticsClickEvent('Add to');
            },
            error => {
              this.errorMessage.next(AddRemoveBidListButtonEnum.ADD);
              this.toggleOnBidList();
            }
          );
      });
  }

  handleRemoveBidListError() {
    this.errorMessage.next(AddRemoveBidListButtonEnum.REMOVE);
  }

  removeItemFromBidList() {
    this.bidListService
      .removeItemFromBidList(this.auctionItemUuid)
      .pipe(
        take(1),
        tap(() => {
          this.toggleOnBidList();
          this.addRemoveAnalyticsClickEvent('Remove from');
        }),
        catchError(response => of(this.handleRemoveBidListError()))
      )
      .subscribe();
  }

  toggleOnBidList() {
    this.isOnBidList = !this.isOnBidList;
    this.changeDetector.markForCheck();
  }

  populateDealershipDropdown() {
    if (!this.buyerOeUuid) {
      this.buyerOeUuid = this.getOrganization().oeUuid;
    }

    this.visibleOrganizationService
      .getVisibleOrganizations(this.auctionItemUuid)
      .pipe(take(1))
      .subscribe(
        data => {
          this.dealershipValues = data.sort((a, b) => a.oeName.localeCompare(b.oeName));
          const dealers = this.dealershipValues.map(dealer => {
            return { name: dealer.oeName, value: dealer.oeUuid, selected: dealer.oeUuid === this.buyerOeUuid };
          });
          if (!dealers.some(item => item.selected)) {
            dealers[0].selected = true;
          }
          this.dealershipOptions = [{ options: dealers }];
          this.buyerOeUuid = dealers.find(dealer => dealer.selected).value;
        },
        error => {
          console.error(error);
        }
      );

    this.store
      .select(state => state.leadBuyer)
      .pipe(take(1))
      .subscribe(
        leadBuyerState => {
          this.allDealerships = leadBuyerState.availableDealerships.filter(dealer => dealer.oeUuid);
        },
        error => {
          console.error(error);
        }
      );
  }

  populateFinanceSourceDropdown() {
    this.financeSourceService
      .getFinanceSourceListByOeUuid(this.buyerOeUuid)
      .pipe(take(1))
      .subscribe(
        financeSources => {
          this.financeSourceValues = financeSources;
          const sources = this.financeSourceValues.map(source => {
            return {
              name: source.financeSourceName,
              value: source.financeSourceUuid,
              selected: source.defaultIndicator
            };
          });
          if (!sources.some(item => item.selected)) {
            sources[0].selected = true;
          }
          this.financeSourceOptions = [{ options: sources }];
          this.selectedFinanceSource = sources.find(source => source.selected).value;
        },
        error => {
          console.error(error);
        }
      );
  }

  onOpenChange(e) {
    this.enableFocusTrap = e;
    this.inModal = !e;
  }

  addRemoveAnalyticsClickEvent(action: string) {
    if (action === 'Add to') {
      this.analyticsService.fireBuyActionEvent('addBidListBuyAction', 1, [this.inventoryItemUuid]);
    } else {
      this.analyticsService.fireBuyActionEvent('removeBidListBuyAction', 1, [this.inventoryItemUuid]);
    }

    const actionText = action === 'Add to' ? 'Add' : 'Remove';
    const toOrFrom = action === 'Add to' ? 'to' : 'from';

    const trackingLabel = `${actionText} ${this.inventoryItemUuid} ${toOrFrom} bid list - MOA`;

    this.analyticsService.track('clickEvent', {
      tagName: 'BUTTON',
      allytmln: trackingLabel,
      linkName: trackingLabel
    });
  }
}
